// src/App.js

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Import contexts and components
import { AuthProvider } from './contexts/AuthContext';
import RequireAuth from './components/RequireAuth';
import RequireBusinessAdmin from './components/RequireBusinessAdmin'; // You need to create this

// Import pages
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/users/UsersPage';
import CustomersPage from './pages/customers/CustomersPage';
import TransactionsPage from './pages/transactions/TransactionsPage';
import TransactionDetailsPage from './pages/transactions/TransactionDetailsPage';
import RolesPage from './pages/roles/RolesPage';
import PermissionsPage from './pages/permissions/PermissionsPage';
import CategoriesPage from './pages/categories/CategoriesPage';
import ProductsPage from './pages/products/ProductsPage';
import ProfilePage from './pages/ProfilePage'; // New Import
import SettingsPage from './pages/SettingsPage'; // New Import
import AttributesPage from './pages/attributes/AttributesPage'; // New Import
import InventoryPage from './pages/inventory/InventoryPage';
import StoreInventoryReportPage from './pages/inventory/StoreInventoryReportPage'; // <-- Import the new page
import InventoryReportPage from './pages/inventory/InventoryReportPage'; // <-- Import consolidated inventory report
import StockHistoryPage from './pages/stock-history/StockHistoryPage'; // New Import
import StoresPage from './pages/stores/StoresPage'; // New Import for Stores
import WarehousesPage from './pages/warehouses/WarehousesPage'; // New Import for Warehouses
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AdminSubscriptionTransactions from './pages/subscription/AdminSubscriptionTransactions';
import BusinessAdminDashboardPage from './pages/business-admin/BusinessAdminDashboardPage';
import BusinessAdminUsersPage from './pages/business-admin/BusinessAdminUsersPage';
import BusinessAdminTransactionsPage from './pages/business-admin/BusinessAdminTransactionsPage';
import BusinessAdminSubscriptionsPage from './pages/business-admin/BusinessAdminSubscriptionsPage';
import PlatformSettingsPage from './pages/business-admin/PlatformSettingsPage';
import BusinessAdminSettingsPage from './pages/business-admin/BusinessAdminSettingsPage';
import ProductBulkUpload from './pages/products/ProductBulkUpload';
import GenerateLabelsPage from './pages/labels/GenerateLabelsPage';
import BatchesPage from './pages/batches/BatchesPage';
import BatchBulkUploadPage from './pages/batches/BatchBulkUploadPage';
import InventoryTransfersPage from './pages/inventoryTransfers/InventoryTransfersPage';
import InventoryTransferDetailsPage from './pages/inventoryTransfers/InventoryTransferDetailsPage';
import PurchaseOrdersPage from './pages/purchaseOrders/PurchaseOrdersPage';
import PromotionsPage from './pages/promotions/PromotionsPage';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

          {/* Business Admin (Vriddhi Team) Routes */}
          <Route
            path="/business-admin/dashboard"
            element={
              <RequireBusinessAdmin>
                <BusinessAdminDashboardPage />
              </RequireBusinessAdmin>
            }
          />
          <Route
            path="/business-admin/users"
            element={
              <RequireBusinessAdmin>
                <BusinessAdminUsersPage />
              </RequireBusinessAdmin>
            }
          />
          <Route
            path="/business-admin/transactions"
            element={
              <RequireBusinessAdmin>
                <BusinessAdminTransactionsPage />
              </RequireBusinessAdmin>
            }
          />
          <Route
            path="/business-admin/subscriptions"
            element={
              <RequireBusinessAdmin>
                <BusinessAdminSubscriptionsPage />
              </RequireBusinessAdmin>
            }
          />
          <Route
            path="/business-admin/settings"
            element={
              <RequireBusinessAdmin>
                <BusinessAdminSettingsPage />
              </RequireBusinessAdmin>
            }
          />

          {/* Organization Admin/Manager Routes */}
          <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
          <Route path="/users" element={<RequireAuth requiredPermission="manage_users"><UsersPage /></RequireAuth>} />
          <Route path="/customers" element={<RequireAuth requiredPermission="manage_customers"><CustomersPage /></RequireAuth>} />
          <Route path="/transactions" element={<RequireAuth requiredPermission="manage_transactions"><TransactionsPage /></RequireAuth>} />
          <Route path="/transactions/:id" element={<RequireAuth requiredPermission="manage_transactions"><TransactionDetailsPage /></RequireAuth>} />
          <Route path="/roles" element={<RequireAuth requiredPermission="manage_roles"><RolesPage /></RequireAuth>} />
          <Route path="/permissions" element={<RequireAuth requiredPermission="manage_permissions"><PermissionsPage /></RequireAuth>} />
          <Route path="/categories" element={<RequireAuth requiredPermission="manage_categories"><CategoriesPage /></RequireAuth>} />
          <Route path="/products" element={<RequireAuth requiredPermission="manage_products"><ProductsPage /></RequireAuth>} />
          <Route path="/attributes" element={<RequireAuth requiredPermission="manage_products"><AttributesPage /></RequireAuth>} />
          <Route path="/inventory" element={<RequireAuth requiredPermission="manage_products"><InventoryPage /></RequireAuth>} />
          <Route path="/inventory/by-store" element={<RequireAuth requiredPermission="manage_products"><StoreInventoryReportPage /></RequireAuth>} /> {/* <-- Add this route */}
          <Route path="/inventory/report" element={<RequireAuth requiredPermission="manage_products"><InventoryReportPage /></RequireAuth>} /> {/* <-- Consolidated inventory report */}
          <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth requiredPermission="manage_settings"><SettingsPage /></RequireAuth>} />
          <Route path="/stock-history" element={<RequireAuth requiredPermission="manage_settings"><StockHistoryPage /></RequireAuth>} />
          <Route path="/stores" element={<RequireAuth requiredPermission="manage_settings"><StoresPage /></RequireAuth>} />
          <Route path="/warehouses" element={<RequireAuth requiredPermission="manage_warehouses"><WarehousesPage /></RequireAuth>} />
          <Route path="/subscription-transactions" element={<RequireAuth><AdminSubscriptionTransactions /></RequireAuth>} />
          <Route path="/products/bulk-upload" element={<RequireAuth><ProductBulkUpload /></RequireAuth>} />
          <Route path="/labels/generate" element={<RequireAuth><GenerateLabelsPage /></RequireAuth>} />
          <Route path="/inventory-transfers" element={<InventoryTransfersPage />} />
          <Route path="/inventory-transfers/:id" element={<RequireAuth requiredPermission="manage_inventory_transfers"><InventoryTransferDetailsPage /></RequireAuth>} />
          <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="/promotions" element={<PromotionsPage />} />
          <Route path="/batches" element={<BatchesPage />} />
          <Route path="/batches/upload" element={<BatchBulkUploadPage />} />
          {/* Default redirect */}
          <Route path="*" element={<RequireAuth><Navigate to="/dashboard" replace /></RequireAuth>} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
